import { AiOutlineExclamationCircle } from 'react-icons/ai'
import { v4 as uuidv4 } from 'uuid'
import { useForm } from 'react-hook-form'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { WarningModal } from 'components/Modals/WarningModal'
import NotificationActions from 'actions/Notification'
import { useDispatch, useSelector } from 'react-redux'
import NotificationService from 'services/Notification'
import { useCallbackPrompt } from 'hooks/useCallbackPrompt'
import { Beforeunload } from 'react-beforeunload'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { BackButton, ContainerForm, MainForm, Top, TopInputs, SaveButton, TitleForm, SubtitleForm } from './styles'
import { Breadcrumbs, SecondTextBreadcrumbs } from '../../NotificationsTreatmentsSteps/Header/styles'
import { GroupActionsComponent } from './Children/ActionsComponent'
import { IAuthenticationReducer } from 'types/autentication'
import { RootReducerState } from 'types/global'
import { notificationStatus } from 'constants/notificationStatus'
import { NotificationAction, NotificationReducer } from 'types/notification'
import { Dispatch } from 'redux'
import GroupDetails from './Children/GroupDetails'
import { hasEmptyValue } from 'utils/objectEmptyValue'
import {
  GroupData,
  convertGroupActionPlanFieldsToUpdateBatchRequest,
  buildActionPlanListsByNotificationMap,
  mergeFirstNotificationActionPlansFromRedux,
  isGroupActionPlanFormEffectivelyEmpty,
  collectAllActionPlanSysIdsFromMap,
} from './helpers'

export const GroupActionForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const notificationDispatch: Dispatch<NotificationAction> = useDispatch()

  const [canSave, setCanSave] = useState(false)
  const [statusIsArchivedOrFinished, setStatusIsArchivedOrFinished] = useState(false)
  const [notificationStatusState, setNotificationStatusState] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [groupData, setGroupData] = useState<GroupData | null>(null)
  const [loadingGroupData, setLoadingGroupData] = useState(true)

  const {
    trigger,
    register,
    control,
    formState: { isDirty, dirtyFields, errors, },
    getValues,
    reset,
    watch,
    unregister,
    setValue,
    resetField,
    handleSubmit,
  } = useForm({
    defaultValues: {
      fields: [
        {
          sys_id: '',
          id: uuidv4(),
          how: '',
          what: '',
          when: "",
          where: '',
          who: '',
          why: '',
          how_much: 'R$ 0,00'
        }
      ]
    }
  })
  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(isDirty && !isSaving)

  const watchedData = watch();
  const auth: IAuthenticationReducer = useSelector(
    (state: RootReducerState) => state.authenticationReducers
  )
  const notificationSelector: NotificationReducer = useSelector((state: RootReducerState) => state.notificationsReducer)

  useEffect(() => {
    if (notificationSelector?.notificationById?.u_state) {
      setNotificationStatusState(
        notificationSelector?.notificationById?.u_state.display_value
      )
    }
  }, [notificationSelector.notificationById])
  useEffect(() => {
    if (notificationStatusState) {
      const allowed = [notificationStatus[4].label, notificationStatus[5].label]
      const satus = allowed.includes(notificationStatusState)
      setStatusIsArchivedOrFinished(satus)
    }
  }, [notificationStatusState])


  useEffect(() => {
    if (!notificationSelector?.notificationById) {
      NotificationActions.getNotificationById(notificationDispatch, id)
    }
  }, [id, notificationDispatch])

  // Buscar dados do grupo
  useEffect(() => {
    const fetchGroupData = async () => {
      if (!id) return

      try {
        setLoadingGroupData(true)
        const groupResponse = await NotificationService.getGroupDataById(id)
        setGroupData(groupResponse)
      } catch (error) {
        console.error('Erro ao buscar dados do grupo:', error)
      } finally {
        setLoadingGroupData(false)
      }
    }

    fetchGroupData()
  }, [id])

  // Buscar dados do plano de ação da primeira notificação
  useEffect(() => {
    // Buscar TODOS os planos de ação da primeira notificação do grupo
    if (groupData?.notifications?.length > 0) {
      const firstNotificationId = groupData.notifications[0]?.sys_id?.value
      if (firstNotificationId) {
        // Buscar todos os planos da primeira notificação
        NotificationActions.getListOfActionPlan(notificationDispatch, firstNotificationId)
      }
    } else {
      // Se não temos dados do grupo ainda, usar busca de grupo como fallback
      NotificationActions.getListOfActionPlanGroup(notificationDispatch, id)
    }
  }, [groupData, id, notificationDispatch])

  const currentUser = useMemo(() => {
    return auth.dataJwt
  }, [auth])

  const actionPlans = useMemo(
    () => {
      // Priorizar planos individuais da primeira notificação sobre planos de grupo
      const individualPlans = notificationSelector?.actionPlans || []
      const groupPlans = notificationSelector?.actionPlansGroup || []


      const selectedPlans = individualPlans.length > 0 ? individualPlans : groupPlans

      return selectedPlans
    },
    [notificationSelector?.actionPlans, notificationSelector?.actionPlansGroup]
  )

  const backToLondonAction = useCallback((e: any) => {
    navigate(`/notificacoes/group-treatment/${id}`, { state: { currentStep: 5 } })
  }, [])

  const handleViewNotification = useCallback((notificationId: string) => {
    if (!notificationId || !groupData) return

    // Coletar todos os sys_ids das notificações do grupo
    const notificationIds = groupData.notifications
      ?.map(notification => notification.sys_id?.value)
      .filter(id => id) || []

    navigate(`/notificacoes/view-group`, {
      state: {
        fromGroup: true,
        groupId: id,
        notificationIds: notificationIds,
        initialNotificationId: notificationId,
        fromActionPlanEdit: true  // Indicar que está vindo da edição do plano de ação
      }
    })
  }, [groupData, id, navigate])


  useEffect(() => {
    if (!isDirty) {
      setCanSave(false)
    } else {
      const watchedFields = watchedData.fields as { sys_id?: string; id?: string }[] | undefined
      if (isGroupActionPlanFormEffectivelyEmpty(watchedFields as any)) {
        setCanSave(true)
        return
      }
      let allFieldsNonEmpty = true

      for (const key in watchedFields) {
        const action = (watchedFields as any)[key]
        const when = action.when
        var regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19|20)\d{2}$/
        const isValidDate = regex.test(when)
        const doesHaveEmpty = hasEmptyValue(action, ['sys_id'])
        if (doesHaveEmpty || !isValidDate) {
          allFieldsNonEmpty = false
          break
        }
      }
      setCanSave(allFieldsNonEmpty)
    }
  }, [watchedData, isDirty])
  const saveStep4Handler = async () => {
    setIsSaving(true)

    if (!groupData || !currentUser) {
      console.error('Dados do grupo ou usuário não encontrados')
      setIsSaving(false)
      return
    }

    const forumValues = getValues()
    const allActionPlans = forumValues?.fields || []
    const committingEmpty = isGroupActionPlanFormEffectivelyEmpty(allActionPlans)

    if (!committingEmpty) {
      const doesNotHaveError = await trigger()
      if (!doesNotHaveError) {
        setIsSaving(false)
        return
      }
    }

    try {
      const templatePlansForMerge =
        (notificationSelector.actionPlans?.length
          ? notificationSelector.actionPlans
          : notificationSelector.actionPlansGroup) ?? null

      if (committingEmpty) {
        const plansByNotif = await buildActionPlanListsByNotificationMap(groupData)
        const mergedPlansByNotif = mergeFirstNotificationActionPlansFromRedux(
          groupData,
          plansByNotif,
          templatePlansForMerge
        )
        const sysIdsToDelete = collectAllActionPlanSysIdsFromMap(mergedPlansByNotif)
        if (sysIdsToDelete.length > 0) {
          await NotificationActions.deleteActionPlanBatch(notificationDispatch, {
            records: sysIdsToDelete as any,
            options: { stopOnError: false },
          })
        }
        NotificationActions.getListOfActionPlanGroup(notificationDispatch, id)
        const firstId = groupData.notifications[0]?.sys_id?.value
        if (firstId) {
          NotificationActions.getListOfActionPlan(notificationDispatch, firstId)
        }
        navigate(`/notificacoes/group-treatment/${id}`, {
          state: {
            currentStep: 5,
            messageSuccess: 'Plano de ação em massa salvo com sucesso!',
          },
        })
        return
      }

      const plansByNotif = await buildActionPlanListsByNotificationMap(groupData)
      const mergedPlansByNotif = mergeFirstNotificationActionPlansFromRedux(
        groupData,
        plansByNotif,
        templatePlansForMerge
      )

      const hasRowsToUpdate = allActionPlans.some(
        (plan) => plan.sys_id && plan.sys_id.trim() !== ''
      )
      if (hasRowsToUpdate) {
        const updateBatchRequest = convertGroupActionPlanFieldsToUpdateBatchRequest(
          forumValues.fields,
          groupData,
          mergedPlansByNotif,
          currentUser
        )
        if (updateBatchRequest.records.length > 0) {
          await NotificationActions.updateActionPlanBatch(
            notificationDispatch,
            updateBatchRequest
          )
        }
      }

      const newPlans = allActionPlans.filter(
        (plan) => !plan.sys_id || plan.sys_id.trim() === ''
      )

      // Processar criações se houver planos novos
      if (newPlans.length > 0) {
        const createRecords = []

        groupData.notifications
          .filter(notification => notification.selected !== false)
          .forEach(notification => {
            newPlans.forEach(actionPlan => {
              createRecords.push({
                // SEM sys_id para CREATE
                hospital_incident: notification.sys_id?.value || '',
                how: actionPlan.how || '',
                how_much: actionPlan.how_much || '',
                what: actionPlan.what || '',
                when: actionPlan.when || '',
                where: actionPlan.where || '',
                who: actionPlan.who || '',
                why: actionPlan.why || '',
                u_notifica_user: currentUser?.sys_id || groupData.group_info.u_notifica_user?.value || '',
                u_do_not_perform_optional: 'false'
              })
            })
          })

        const createBatchRequest = {
          records: createRecords,
          options: { stopOnError: false }
        }

        await NotificationActions.createActionPlanBatch(notificationDispatch, createBatchRequest)
      }

      // Recarregar a lista de action plans do grupo
      NotificationActions.getListOfActionPlanGroup(
        notificationDispatch,
        id
      )

      // Navegar de volta com mensagem de sucesso
      navigate(`/notificacoes/group-treatment/${id}`, {
        state: {
          currentStep: 5,
          messageSuccess: 'Plano de ação em massa salvo com sucesso!'
        }
      })
    } catch (error) {
      console.error('Erro ao salvar plano de ação em massa:', error)
      // TODO: Implementar feedback de erro visual para o usuário
    } finally {
      setIsSaving(false)
    }
  }
  return (
    <>
      {isDirty && (
        <Beforeunload onBeforeunload={(event) => 'Você fez edições que não forma salvas.'} />
      )}
      <WarningModal
        icon={
          <AiOutlineExclamationCircle
            size={50}
            className="text-yellow"
          />
        }
        customStyle={{
          titleStyle: 'text-yellow'
        }}
        title="Edição não salva"
        subTitle="Você fez edições que não foram salvas. Se sair, você perderá as edições."
        leftButtonAction={confirmNavigation as () => void}
        rightButtonAction={cancelNavigation as () => void}
        isOpen={showPrompt as boolean}
        handleCloseModal={cancelNavigation as () => void}
        leftButtonText="Sair"
        rightButtonText="Continuar edição"
      />

      <ContainerForm data-testid="test-group-step4-from">
        <MainForm onSubmit={handleSubmit(saveStep4Handler)}>
          <div className='flex items-baseline mt-8 justify-between'>
            <Breadcrumbs>
              <Link className="mr-1 cursor-pointer" to="/notificacoes">
                Gestão das notificações
              </Link>
              {' > '}
              <span className="mr-1 cursor-pointer" onClick={backToLondonAction}>
                Tratamento em Grupo
              </span>
              {' > '}
              <SecondTextBreadcrumbs>Plano de ação</SecondTextBreadcrumbs>
            </Breadcrumbs>
            <TopInputs>
              <BackButton type='button' onClick={backToLondonAction}>Voltar</BackButton>
              <SaveButton type='submit' disabled={!canSave} className={!canSave ? 'bg-disabled' : ''}>Salvar</SaveButton>
            </TopInputs>
          </div>
          <div className='overflow-y-auto'>
            <Top>
              <div>
                <TitleForm data-testid='group-notifications'>Editar Plano de ação em Massa</TitleForm>
                <SubtitleForm>Descreva quais os planos de ação do grupo para a resolução de incidentes desse tipo.</SubtitleForm>
              </div>


            </Top>
            <div className="grid grid-cols-6  px-8 mt-6 gap-10">
              <div className="col-span-6 xl:col-span-4 ">
                <GroupActionsComponent
                  id={id}
                  trigger={trigger}
                  tooltipAlert={''}
                  register={register}
                  control={control}
                  reset={reset}
                  getValues={getValues}
                  watch={watch}
                  errors={errors}
                  currentUser={currentUser}
                  groupData={groupData}
                  dirtyFieldsStep4={dirtyFields}
                  setValue={setValue}
                  resetField={resetField}
                  isDirty={isDirty}
                  statusIsArchivedOrFinished={statusIsArchivedOrFinished}
                  actionPlans={actionPlans}
                  notificationById={notificationSelector?.notificationById}
                  notificationStatusState={notificationStatusState}
                />
              </div>
              <div className="col-span-6 xl:col-span-2">
                <GroupDetails
                  groupData={groupData}
                  onViewNotification={handleViewNotification}
                />
              </div>
            </div>
          </div>
        </MainForm>
      </ContainerForm>
    </>
  )
}